'use client';

import Button, { ButtonVariants } from '@/components/atoms/Button/Button';
import GridContainer from '@/components/atoms/ResponsiveGrid/GridContainer';
import GridItem from '@/components/atoms/ResponsiveGrid/GridItem';
import TextButton from '@/components/atoms/TextButton/TextButton';
import Grid from '@/components/atoms/legacy/Grid';
import Heading, { HeadingLevels } from '@/components/atoms/legacy/Heading';
import { ResponsiveMargin } from '@/components/atoms/legacy/ResponsiveMargin/ResponsiveMargin';
import { SearchBarWithMenu } from '@/components/molecules/SearchBarWithMenu/SearchBarWithMenu';
import { useZipContext } from '@/context/ZipContext';
import { useSaveRecentSearchMutation } from '@/hooks/queries/recent-searches';
import { GOOGLE_TRACK_INFO } from '@/lib/constants';
import { BodyTypeOption, ErrorPageProps } from '@/lib/schema/inventory/types';
import { handleFilteredSearch } from '@/lib/searchFilters';
import { SEOMetaTags } from '@/lib/seo/seoTags';
import { useRouter } from 'next/compat/router';
import { useState } from 'react';
import { BodyTypeIcon } from '../../atoms/BodyTypeIcon';

const popularList = [
  { label: 'All Electric Vehicles', queryUrl: '/fuelType/Electric' },
  { label: 'Chevrolet Bolt EV', queryUrl: '/make/Chevrolet/model/Bolt%20EV' },
  {
    label: 'Ford F-150 Lightning',
    queryUrl: '/make/Ford/model/F-150%20Lightning',
  },
  { label: 'All Plug-in Hybrid Vehicles', queryUrl: '/fuelType/Hybrid' },
  { label: 'Subaru Solterra', queryUrl: '/make/Subaru/model/Solterra' },
  {
    label: 'Ford Mustang Mach-E',
    queryUrl: '/make/Ford/model/Mustang%20Mach-E',
  },
];

const ALL_BODY_TYPE_OPTIONS: BodyTypeOption[] = [
  {
    icon: {
      iconId: 'Sedan',
      className: 'h-[24px] w-[61px]',
    },
    value: 'Sedan',
  },
  {
    icon: {
      iconId: 'Suv',
      className: 'h-[24px] w-[61px]',
    },
    value: 'SUV',
  },
  {
    icon: {
      iconId: 'Hatchback',
      className: 'h-[24px] w-[61px]',
    },
    value: 'Hatchback',
  },
  {
    icon: {
      iconId: 'Truck',
      className: 'h-[24px] w-[61px]',
    },
    value: 'Truck',
  },
  {
    icon: {
      iconId: 'Wagon',
      className: 'h-[24px] w-[61px] ',
    },
    value: 'Wagon',
  },
  {
    icon: {
      iconId: 'Coupe',
      className: 'h-[24px] w-[61px]',
    },
    value: 'Coupe',
  },
];

export const ErrorTemplate = ({ autoCompleteResults }: ErrorPageProps) => {
  const router = useRouter();
  const [input, setInput] = useState<string>('');
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const { preferredZip } = useZipContext();
  const { mutateAsync: saveRecentSearch } = useSaveRecentSearchMutation();

  return (
    <>
      <ResponsiveMargin>
        <GridContainer className="w-full justify-center">
          <GridItem xs={12} m={8} colStartXs={0} colStartM={3}>
            <div className="p-10 text-center">
              <Heading level={HeadingLevels.H1} className="mb-2 text-h4Regular">
                {SEOMetaTags.errorpage.h1Tag}
              </Heading>
              <Heading level={HeadingLevels.H2} className="text-h5Regular">
                {SEOMetaTags.errorpage.h2Tag}
              </Heading>
            </div>
            <div className="flex w-full flex-col justify-center gap-[40px] rounded-lg bg-white p-[40px]">
              <div className="flex flex-col">
                <div className="text-h4Regular text-neutral-800">Search</div>
                <div className="flex w-full flex-col gap-m rounded-lg bg-white py-m ml:h-[72px] ml:flex-row">
                  <div className="inline-flex h-[48px] w-full">
                    <SearchBarWithMenu
                      input={input}
                      setInput={setInput}
                      models={autoCompleteResults.searchModels}
                      makes={autoCompleteResults.searchMakes}
                      setShowOptions={setShowOptions}
                      showOptions={showOptions}
                    />
                  </div>

                  <div className="flex h-[48px] w-full ml:h-full ml:w-1/5">
                    <Button
                      aria-label="Search EVs"
                      variant={ButtonVariants.Tertiary}
                      analyticsEvent={{
                        ...GOOGLE_TRACK_INFO.inventoryHomePageButton,
                        pageTarget: 'search',
                      }}
                      onClick={async () => {
                        await saveRecentSearch(input);
                        handleFilteredSearch({
                          input,
                          zip: preferredZip,
                        });
                      }}
                    >
                      Search EVs
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-s">
                <div className="text-h4Regular text-neutral-800">Popular</div>
                <Grid className="!grid-cols-1 m:!grid-cols-2 l:!grid-cols-3">
                  {popularList.map((item, index) => {
                    return (
                      <TextButton
                        key={index}
                        aria-label="view full spec list"
                        onClick={
                          router?.isReady
                            ? () => router.push(`/search/${item.queryUrl}`)
                            : undefined
                        }
                      >
                        {item.label}
                      </TextButton>
                    );
                  })}
                </Grid>
              </div>
              <div className="flex flex-col gap-l">
                <div className="text-h4Regular text-neutral-800">Body type</div>
                <Grid className="!grid-cols-2 gap-y-l m:!grid-cols-3">
                  {ALL_BODY_TYPE_OPTIONS.map((option, index) => {
                    return (
                      <div
                        role={router?.isReady ? 'button' : undefined}
                        onClick={() =>
                          router?.isReady
                            ? router.push(`/search/body/${option.value}`)
                            : undefined
                        }
                        key={`${option.icon.iconId}-${index}`}
                        aria-label={`filter by ${option.value}`}
                        className="flex cursor-pointer flex-col items-center justify-center gap-s rounded-small border border-neutral-100 bg-neutral-100 p-l hover:border-brandElectric hover:bg-green-light hover:text-brandMidnight"
                      >
                        <BodyTypeIcon
                          bodyType={option.icon.iconId}
                          className={option.icon.className}
                        />
                        <div className="text-body1Light text-neutral-800">
                          {option.value}
                        </div>
                      </div>
                    );
                  })}
                </Grid>
              </div>
            </div>
          </GridItem>
        </GridContainer>
      </ResponsiveMargin>
    </>
  );
};
